import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { useStore } from "../store";
import type { Profile } from "../types";
import { AlertCircle } from 'lucide-react';
import { useRipple } from '../lib/useRipple';
import { useTranslation } from '../i18n/useTranslation';

declare global {
  interface Window {
    grecaptcha: {
      enterprise: {
        ready: (callback: () => void) => void;
        execute: (
          siteKey: string,
          options: { action: string }
        ) => Promise<string>;
        render: (container: string | HTMLElement, parameters: object) => number;
      };
    };
  }
}

export default function Register() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [age, setAge] = useState('');
  const [level, setLevel] = useState('');
  const [team, setTeam] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const setUser = useStore((state) => state.setUser);
  const navigate = useNavigate();
  const { createRipple } = useRipple();

  const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    const loadRecaptchaScript = () => {
      if (!document.querySelector('script[src*="recaptcha/enterprise.js"]')) {
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/enterprise.js?render=${RECAPTCHA_SITE_KEY}`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
          setRecaptchaLoaded(true);
          window.grecaptcha.enterprise.ready(() => {
            // reCAPTCHA Enterprise ready
          });
        };
        document.head.appendChild(script);
      } else if (window.grecaptcha && window.grecaptcha.enterprise) {
        setRecaptchaLoaded(true);
      }
    };

    loadRecaptchaScript();

    return () => {
      // Cleanup if needed
    };
  }, [RECAPTCHA_SITE_KEY]);

  const executeRecaptcha = async () => {
    if (!window.grecaptcha || !window.grecaptcha.enterprise) {
      console.error('reCAPTCHA not loaded');
      return null;
    }

    try {
      const token = await window.grecaptcha.enterprise.execute(
        RECAPTCHA_SITE_KEY,
        { action: 'register' }
      );
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution error:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      if (recaptchaLoaded) {
        const token = await executeRecaptcha();
        if (!token) {
          setError('Security verification failed. Please try again.');
          setLoading(false);
          return;
        }
      }

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      const profileData: Omit<Profile, 'id'> = {
        firstName,
        lastName,
        age: parseInt(age, 10),
        level,
        team,
        createdAt: new Date(),
      };

      await setDoc(doc(db, 'profiles', user.uid), profileData);

      setUser(user);

      // Navigate directly to dashboard instead of payment
      navigate('/dashboard');
    } catch (err) {
      console.error('Registration error:', err);
      if (err instanceof Error) {
        if (err.message.includes('email-already-in-use')) {
          setError(
            'This email is already registered. Please use a different email or sign in.'
          );
        } else if (err.message.includes('weak-password')) {
          setError('Password should be at least 6 characters long.');
        } else if (err.message.includes('reCAPTCHA')) {
          setError('Security verification failed. Please try again.');
        } else {
          setError('Failed to create account. Please try again.');
        }
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className='min-h-screen pt-16 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8'
      style={{
        backgroundImage: `url(/GOALR-Login-background.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className='glass-card max-w-md w-full p-8 animate-float'>
        <Link
          to='/'
          className='block text-center mb-8 transition-colors hover:scale-105 transform duration-200'
        >
          <img
            src='https://cdn.shopify.com/s/files/1/0609/6397/9463/files/Logo_Goalr.png?v=1740666570'
            alt='GoalR Logo'
            className='h-14 w-auto mx-auto'
          />
        </Link>
        <h2 className='text-3xl font-bold text-center mb-8 text-primary-600 italic'>
          {t('auth.createAccount')}
        </h2>
        {error && (
          <div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-start'>
            <AlertCircle className='h-5 w-5 text-red-500 mr-2 mt-0.5 flex-shrink-0' />
            <span>{error}</span>
          </div>
        )}
        <form onSubmit={handleSubmit} className='space-y-6'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label
                htmlFor='firstName'
                className='block text-sm font-medium text-heading mb-2'
              >
                {t('auth.firstName')}
              </label>
              <input
                type='text'
                id='firstName'
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className='w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600 focus:ring-2 transition-all duration-200'
                required
                disabled={loading}
              />
            </div>
            <div>
              <label
                htmlFor='lastName'
                className='block text-sm font-medium text-heading mb-2'
              >
                {t('auth.lastName')}
              </label>
              <input
                type='text'
                id='lastName'
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className='w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600 focus:ring-2 transition-all duration-200'
                required
                disabled={loading}
              />
            </div>
          </div>
          <div>
            <label
              htmlFor='email'
              className='block text-sm font-medium text-heading mb-2'
            >
              {t('auth.email')}
            </label>
            <input
              type='email'
              id='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className='w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600 focus:ring-2 transition-all duration-200'
              required
              disabled={loading}
            />
          </div>
          <div>
            <label
              htmlFor='password'
              className='block text-sm font-medium text-heading mb-2'
            >
              {t('auth.password')}
            </label>
            <input
              type='password'
              id='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className='w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600 focus:ring-2 transition-all duration-200'
              required
              minLength={6}
              disabled={loading}
            />
          </div>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <label
                htmlFor='age'
                className='block text-sm font-medium text-heading mb-2'
              >
                {t('auth.age')}
              </label>
              <input
                type='number'
                id='age'
                value={age}
                onChange={(e) => setAge(e.target.value)}
                className='w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600 focus:ring-2 transition-all duration-200'
                required
                min={1}
                disabled={loading}
              />
            </div>
            <div>
              <label
                htmlFor='level'
                className='block text-sm font-medium text-heading mb-2'
              >
                {t('auth.level')}
              </label>
              <select
                id='level'
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                className='w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600 focus:ring-2 transition-all duration-200'
                required
                disabled={loading}
              >
                <option value=''>{t('auth.selectLevel')}</option>
                <option value='Recreational'>{t('auth.recreational')}</option>
                <option value='Junior'>{t('auth.junior')}</option>
                <option value='High School'>{t('auth.highSchool')}</option>
                <option value='College'>{t('auth.college')}</option>
                <option value='Semi-Professional'>{t('auth.semiProfessional')}</option>
                <option value='Professional'>{t('auth.professional')}</option>
              </select>
            </div>
          </div>
          <div>
            <label
              htmlFor='team'
              className='block text-sm font-medium text-heading mb-2'
            >
              {t('auth.team')}
            </label>
            <input
              type='text'
              id='team'
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              className='w-full px-4 py-3 rounded-lg border border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600 focus:ring-2 transition-all duration-200'
              required
              disabled={loading}
            />
          </div>

          {!recaptchaLoaded && (
            <div className='text-amber-600 text-sm text-center'>
              Loading security verification...
            </div>
          )}

          <div className='text-xs text-gray-500 mt-2'>
            This site is protected by reCAPTCHA Enterprise and the Google
            <a
              href='https://policies.google.com/privacy'
              className='text-[#1b3968] hover:underline'
              target='_blank'
              rel='noopener noreferrer'
            >
              {' '}
              Privacy Policy
            </a>{' '}
            and
            <a
              href='https://policies.google.com/terms'
              className='text-[#1b3968] hover:underline'
              target='_blank'
              rel='noopener noreferrer'
            >
              {' '}
              Terms of Service
            </a>{' '}
            apply.
          </div>

          <button
            type='submit'
            onClick={createRipple}
            className='btn-mui-primary btn-ripple w-full mt-6'
            disabled={loading || !recaptchaLoaded}
          >
            {loading ? t('auth.creatingAccount') : t('auth.createAccount')}
          </button>
        </form>
        <p className='mt-6 text-center text-muted'>
          Already have an account?{' '}
          <Link
            to='/login'
            className='text-primary-600 hover:text-primary-700 font-medium transition-colors duration-200'
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
